import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  IonPage,
  IonContent,
  IonButton,
  IonItem,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonNote,
  IonText,
  IonSpinner,
  IonIcon,
  IonChip,
} from '@ionic/react';
import {
  person,
  business,
  people,
  arrowBack,
  arrowForward,
  checkmark,
  checkmarkCircle,
  close,
  create,
  sparkles,
  mail,
  lockClosed,
  personOutline,
  briefcase,
  location,
  time,
  construct,
  card,
} from 'ionicons/icons';
import { useMachine } from '@xstate/react';
import {
  registrationMachine,
  type RegistrationRole,
  type ProfileType,
} from '@/lib/workflow/machines/registrationMachine';
import {
  accountSchema,
  basicProfileSchema,
  skillsSchema,
  nationalIdSchema,
  nationalIdTypeEnum,
  nationalIdLabels,
  availabilityEnum,
  availabilityLabels,
  workTypeEnum,
  workTypeLabels,
  PROVINCES,
} from '@/lib/validation/registration';
import { submitRegistration } from '@/lib/api/registration';
import type { ApiError } from '@/lib/api/client';

const REG_ERROR_MAP: Record<string, string> = {
  'already exists': 'An account with this email already exists. Try signing in instead.',
  'email already': 'An account with this email already exists. Try signing in instead.',
  'User not found': 'Could not create your account. Please try again.',
  'weak password': 'Your password is too weak. Use at least 8 characters with a mix of letters and numbers.',
};
import { searchOfoCodes, recordContextView, type OfoSuggestion } from '@/lib/ofo-client-search';
import { QUICK_PICK_SKILLS, loadOccupations } from '@/lib/ofo-occupations';
import { coordsToNearestPlace, loadPlaces, provinceOfFrom, searchPlaces } from '@/lib/geo/places';
import { LogoLink } from '@/components/LogoLink';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';
import { generatePassword } from '@/lib/password-generator';

interface WizardForm {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  profileType: ProfileType;
  nationalIdType: string;
  nationalIdNumber: string;
  headline: string;
  availability: string;
  workType: string;
  province: string;
  city: string;
  skills: string[];
}

const initialForm: WizardForm = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  profileType: 'Individual',
  nationalIdType: 'NationalId',
  nationalIdNumber: '',
  headline: '',
  availability: 'Contract',
  workType: 'Contract',
  province: '',
  city: '',
  skills: [],
};

const STEP_META: Record<string, { label: string; icon: string }> = {
  roleSelection: { label: 'Role', icon: briefcase },
  accountDetails: { label: 'Account', icon: mail },
  profileType: { label: 'Profile', icon: personOutline },
  basicProfile: { label: 'Details', icon: construct },
  nationalId: { label: 'ID', icon: card },
  skillSelection: { label: 'Skills', icon: sparkles },
  review: { label: 'Review', icon: checkmarkCircle },
};

/** Each word capitalised — e.g. "Automotive Motor Mechanic". */
function titleCase(text: string): string {
  if (!text) return text;
  return text.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

export function RegistrationWizard({ onComplete }: { onComplete?: (role?: RegistrationRole) => void }) {
  const [state, send] = useMachine(registrationMachine);
  const [form, setForm] = useState<WizardForm>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [headlineSuggestions, setHeadlineSuggestions] = useState<OfoSuggestion[]>([]);
  const [headlineOpen, setHeadlineOpen] = useState(false);
  const [headlineLoading, setHeadlineLoading] = useState(false);
  const [headlineOfo, setHeadlineOfo] = useState<OfoSuggestion | null>(null);
  const headlineDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [copiedPw, setCopiedPw] = useState(false);

  const handleGeneratePassword = useCallback(() => {
    const pw = generatePassword(3);
    set({ password: pw });
    setShowPassword(true);
    setCopiedPw(false);
  }, []);

  const handleCopyPassword = useCallback(() => {
    navigator.clipboard.writeText(form.password).then(() => {
      setCopiedPw(true);
      setTimeout(() => setCopiedPw(false), 2000);
    });
  }, [form.password]);

  useEffect(() => {
    if (state.matches('idle')) send({ type: 'START' });
  }, [state, send]);

  const value = state.value as string;

  const visibleSteps = useMemo(() => {
    const base = ['roleSelection', 'accountDetails', 'profileType', 'basicProfile', 'nationalId'];
    if (state.context.role !== 'employer') base.push('skillSelection');
    base.push('review');
    return base;
  }, [state.context.role]);

  const stepIndex = Math.max(0, visibleSteps.indexOf(value));
  const isLastFormStep = value === 'review';
  const isComplete = state.matches('complete');
  const isSubmitting = state.matches('submitting');

  // Auto-detect province + city/suburb from the browser's geolocation when the
  // user reaches the profile-details step. Best-effort: silently skips if denied.
  const [geoDetected, setGeoDetected] = useState(false);
  useEffect(() => {
    if (value !== 'basicProfile' || geoDetected) return;
    setGeoDetected(true);
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const place = await coordsToNearestPlace(pos.coords.latitude, pos.coords.longitude);
          if (!place) return;
          const places = await loadPlaces();
          const node = places.find((p) => p.name === place.name && p.placeType === place.placeType);
          const province = node ? provinceOfFrom(places, node) : null;
          setForm((f) => ({
            ...f,
            city: f.city || place.name,
            province: f.province || (province ?? ''),
          }));
        } catch {
          /* detection best-effort */
        }
      },
      () => {
        /* denied — leave fields empty for manual entry */
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
    );
  }, [value, geoDetected]);

  // Skill/trade autocomplete (full OFO via searchOfoCodes) for the "add another skill" input.
  const [skillQuery, setSkillQuery] = useState('');
  const [skillSuggestions, setSkillSuggestions] = useState<OfoSuggestion[]>([]);
  // Quick-pick pills show a CURATED SAMPLE of trades and skills; the full OFO
  // list stays searchable via the autocomplete below.
  const [tradeChips, setTradeChips] = useState<string[]>(QUICK_PICK_SKILLS);

  // Resolve the sample pills to exact OFO descriptions once the dataset loads.
  useEffect(() => {
    loadOccupations()
      .then(() => setTradeChips(QUICK_PICK_SKILLS))
      .catch(() => {});
  }, []);

  const [skillOpen, setSkillOpen] = useState(false);
  const [skillLoading, setSkillLoading] = useState(false);
  // True when a >=2-char query ran and found nothing — the dropdown then shows
  // a "no match, continue without selecting" hint instead of staying closed.
  const [skillNoResults, setSkillNoResults] = useState(false);
  const skillDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skillRef = useRef<HTMLDivElement>(null);

  const runSkillSearch = (q: string) => {
    setSkillQuery(q);
    if (skillDebounce.current) clearTimeout(skillDebounce.current);
    if (q.trim().length < 2) {
      setSkillSuggestions([]);
      setSkillNoResults(false);
      setSkillOpen(false);
      return;
    }
    setSkillLoading(true);
    skillDebounce.current = setTimeout(async () => {
      try {
        // Full OFO classification (hybrid lexical + semantic), NOT the
        // trade-only subset — "web developer" and similar non-trade job
        // descriptions must resolve too.
        const results = await searchOfoCodes(q, 8);
        setSkillSuggestions(results);
        setSkillNoResults(results.length === 0);
        setSkillOpen(true);
      } catch {
        setSkillSuggestions([]);
        setSkillNoResults(true);
        setSkillOpen(true);
      } finally {
        setSkillLoading(false);
      }
    }, 250);
  };

  const addSkill = (name: string) => {
    const s = name.trim();
    if (s && !form.skills.includes(s)) set({ skills: [...form.skills, s] });
    setSkillQuery('');
    setSkillSuggestions([]);
    setSkillNoResults(false);
    setSkillOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (skillRef.current?.contains(e.target as Node)) return;
      setSkillOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Province autocomplete — MUST match an established SA province (PROVINCES).
  const [provinceOpen, setProvinceOpen] = useState(false);
  const provinceRef = useRef<HTMLDivElement>(null);
  const provinceSuggestions = useMemo(() => {
    const q = form.province.trim().toLowerCase();
    if (!q) return PROVINCES;
    return PROVINCES.filter((p) => p.toLowerCase().includes(q));
  }, [form.province]);
  const setProvince = (p: string) => {
    set({ province: p });
    setProvinceOpen(false);
  };
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (provinceRef.current?.contains(e.target as Node)) return;
      setProvinceOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // City / Suburb autocomplete — OpenDB suggestions, but open to free text.
  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const cityDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cityRef = useRef<HTMLDivElement>(null);
  const runCitySearch = (q: string) => {
    set({ city: q });
    if (cityDebounce.current) clearTimeout(cityDebounce.current);
    if (q.trim().length < 2) {
      setCitySuggestions([]);
      setCityOpen(false);
      return;
    }
    setCityLoading(true);
    cityDebounce.current = setTimeout(async () => {
      try {
        const results = await searchPlaces(q, 8);
        setCitySuggestions(results.map((r) => r.name));
        setCityOpen(true);
      } catch {
        setCitySuggestions([]);
      } finally {
        setCityLoading(false);
      }
    }, 250);
  };
  const pickCity = (name: string) => {
    set({ city: name });
    setCitySuggestions([]);
    setCityOpen(false);
  };
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (cityRef.current?.contains(e.target as Node)) return;
      setCityOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const set = (patch: Partial<WizardForm>) => setForm((f) => ({ ...f, ...patch }));

  const validateAccount = () => {
    const r = accountSchema.safeParse({
      email: form.email,
      password: form.password,
      firstName: form.firstName,
      lastName: form.lastName,
    });
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const issue of r.error.issues) e[String(issue.path[0])] = issue.message;
      setErrors(e);
      return false;
    }
    setErrors({});
    return true;
  };

  const validateBasic = () => {
    const r = basicProfileSchema.safeParse({
      headline: form.headline,
      availability: form.availability,
      workType: form.workType,
      province: form.province,
      city: form.city,
    });
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const issue of r.error.issues) e[String(issue.path[0])] = issue.message;
      setErrors(e);
      return false;
    }
    if (form.province && !(PROVINCES as string[]).includes(form.province)) {
      setErrors({ province: 'Choose a province from the list.' });
      return false;
    }
    if (!headlineOfo) {
      setErrors({ headline: 'Pick an occupation from the list — only recognised titles are allowed.' });
      return false;
    }
    setErrors({});
    return true;
  };

  const runHeadlineSearch = (q: string) => {
    if (headlineDebounce.current) clearTimeout(headlineDebounce.current);
    setHeadlineOfo(null);
    if (q.trim().length < 2) {
      setHeadlineSuggestions([]);
      setHeadlineOpen(false);
      return;
    }
    setHeadlineLoading(true);
    headlineDebounce.current = setTimeout(async () => {
      try {
        const results = await searchOfoCodes(q, 6);
        setHeadlineSuggestions(results);
        setHeadlineOpen(true);
      } catch {
        setHeadlineSuggestions([]);
      } finally {
        setHeadlineLoading(false);
      }
    }, 250);
  };

  const selectHeadline = (ofo: OfoSuggestion) => {
    recordContextView(ofo.description);
    setHeadlineOfo(ofo);
    setForm((f) => ({ ...f, headline: titleCase(ofo.description) }));
    setHeadlineSuggestions([]);
    setHeadlineOpen(false);
    setErrors((e) => ({ ...e, headline: '' }));
  };

  const headlineRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (headlineRef.current?.contains(e.target as Node)) return;
      setHeadlineOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const validateSkills = () => {
    if (state.context.role === 'employer') return true;
    const r = skillsSchema.safeParse(form.skills);
    if (!r.success) {
      setErrors({ skills: r.error.issues[0].message });
      return false;
    }
    setErrors({});
    return true;
  };

  const validateNationalId = () => {
    const r = nationalIdSchema.safeParse({
      type: form.nationalIdType,
      number: form.nationalIdNumber,
      country: 'ZA',
    });
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const issue of r.error.issues) {
        const path = issue.path.join('.');
        e[path || 'nationalId'] = issue.message;
      }
      setErrors(e);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleNext = () => {
    switch (value) {
      case 'roleSelection':
        send({ type: 'SELECT_ROLE', role: state.context.role ?? 'candidate' });
        break;
      case 'accountDetails':
        if (!validateAccount()) return;
        send({
          type: 'SUBMIT_ACCOUNT',
          account: {
            email: form.email,
            password: form.password,
            firstName: form.firstName,
            lastName: form.lastName,
          },
        });
        break;
      case 'profileType':
        send({ type: 'SELECT_PROFILE_TYPE', profileType: form.profileType });
        break;
      case 'basicProfile':
        if (!validateBasic()) return;
        send({
          type: 'SUBMIT_PROFILE',
          headline: form.headline,
          availability: form.availability,
          workType: form.workType,
          province: form.province,
          city: form.city,
        });
        break;
      case 'nationalId': {
        // Skip validation if user left it empty (optional step)
        const hasNationalId = form.nationalIdNumber.trim().length > 0;
        if (hasNationalId && !validateNationalId()) return;
        send({
          type: 'SUBMIT_NATIONAL_ID',
          nationalId: hasNationalId
            ? { type: form.nationalIdType, number: form.nationalIdNumber.trim(), country: 'ZA' }
            : null,
        });
        break;
      }
      case 'skillSelection':
        if (!validateSkills()) return;
        send({ type: 'ADD_SKILLS', skills: form.skills });
        break;
      case 'review':
        send({ type: 'SUBMIT' });
        break;
      default:
        break;
    }
  };

  const handleBack = () => send({ type: 'BACK' });

  const toggleSkill = (skill: string) => {
    set({
      skills: form.skills.includes(skill)
        ? form.skills.filter((s) => s !== skill)
        : [...form.skills, skill],
    });
  };

  const roleCards: { key: RegistrationRole; icon: string; title: string; desc: string }[] = [
    { key: 'candidate', icon: person, title: "I'm looking for work", desc: 'Find contract & project jobs as a tradesperson' },
    { key: 'employer', icon: business, title: "I'm hiring", desc: 'Post jobs and find skilled pros' },
    { key: 'both', icon: people, title: 'Both', desc: 'Offer your trade and hire others' },
  ];

  const selectedRole = state.context.role;

  useEffect(() => {
    if (state.matches('submitting')) {
      submitRegistration(state.context)
        .then(() => send({ type: 'SUBMITTED' }))
        .catch((e: unknown) => {
          let msg = e instanceof Error ? e.message : 'Registration failed';
          if (e && typeof e === 'object' && 'status' in e) {
            const apiErr = e as ApiError;
            const key = apiErr.detail || apiErr.message;
            for (const [pattern, friendly] of Object.entries(REG_ERROR_MAP)) {
              if (key.includes(pattern)) { msg = friendly; break; }
            }
            if (apiErr.status === 409) msg = 'An account with this email already exists. Try signing in instead.';
            else if (apiErr.status === 400) msg = msg === apiErr.message ? 'Please check your details and try again.' : msg;
            else if (apiErr.status >= 500) msg = 'Something went wrong on our end. Please try again shortly.';
          }
          send({ type: 'ERROR', message: msg });
        });
    }
  }, [state, send]);

  const renderStep = () => {
    switch (value) {
      case 'idle':
        return (
          <div className="bp-reg-center">
            <IonSpinner />
          </div>
        );
      case 'roleSelection':
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">How will you use BluePages?</h2>
              <p className="bp-reg-step-sub">Pick the option that fits you best — you can change this later.</p>
            </div>
            <div className="bp-reg-role-grid">
              {roleCards.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`bp-reg-role-card ${selectedRole === c.key ? 'selected' : ''}`}
                  onClick={() => send({ type: 'SELECT_ROLE', role: c.key })}
                >
                  <span className="bp-reg-role-icon"><IonIcon icon={c.icon} /></span>
                  <div className="bp-reg-role-text">
                    <div className="bp-reg-role-title">{c.title}</div>
                    <div className="bp-reg-role-desc">{c.desc}</div>
                  </div>
                  {selectedRole === c.key && <IonIcon icon={checkmarkCircle} className="bp-reg-role-check" />}
                </button>
              ))}
            </div>
          </div>
        );
      case 'accountDetails':
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">Create your account</h2>
              <p className="bp-reg-step-sub">Use a real email — we'll send a verification link.</p>
            </div>
            <div className="bp-reg-fields">
              <div className="bp-reg-field">
                <label className="bp-reg-label">Email</label>
                <IonItem className="bp-reg-input" lines="none">
                  <IonIcon icon={mail} slot="start" className="bp-reg-input-icon" />
                  <IonInput type="email" autocomplete="email" placeholder="you@email.com" value={form.email} onIonInput={(e) => set({ email: e.detail.value! })} />
                </IonItem>
                {errors.email && <IonNote color="danger" className="bp-reg-error">{errors.email}</IonNote>}
              </div>
              <div className="bp-reg-field">
                <label className="bp-reg-label">Password</label>
                <IonItem className="bp-reg-input" lines="none">
                  <IonIcon icon={lockClosed} slot="start" className="bp-reg-input-icon" />
                  <IonInput
                    type={showPassword ? 'text' : 'password'}
                    autocomplete="new-password"
                    placeholder="At least 8 characters"
                    value={form.password}
                    onIonInput={(e) => set({ password: e.detail.value! })}
                  />
                  <button
                    slot="end"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ background: 'none', border: 'none', color: 'var(--bp-text-muted, #64748b)', cursor: 'pointer', fontSize: '0.75rem', padding: '4px 6px', fontWeight: 600 }}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </IonItem>
                <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: 'none', border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                      color: 'var(--bp-blue-400, #60a5fa)', fontSize: '0.75rem', fontWeight: 600,
                    }}
                  >
                    ⚡ Generate a memorable password
                  </button>
                  {form.password && (
                    <button
                      type="button"
                      onClick={handleCopyPassword}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        background: 'none', border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                        color: copiedPw ? 'var(--bp-success, #22c55e)' : 'var(--bp-text-muted, #94a3b8)',
                        fontSize: '0.75rem', fontWeight: 600,
                      }}
                    >
                      {copiedPw ? '✓ Copied' : '📋 Copy'}
                    </button>
                  )}
                </div>
                {errors.password && <IonNote color="danger" className="bp-reg-error">{errors.password}</IonNote>}
              </div>
              <div className="bp-reg-row">
                <div className="bp-reg-field">
                  <label className="bp-reg-label">First name</label>
                  <IonItem className="bp-reg-input" lines="none">
                    <IonIcon icon={personOutline} slot="start" className="bp-reg-input-icon" />
                    <IonInput placeholder="Sipho" value={form.firstName} onIonInput={(e) => set({ firstName: e.detail.value! })} />
                  </IonItem>
                  {errors.firstName && <IonNote color="danger" className="bp-reg-error">{errors.firstName}</IonNote>}
                </div>
                <div className="bp-reg-field">
                  <label className="bp-reg-label">Last name</label>
                  <IonItem className="bp-reg-input" lines="none">
                    <IonIcon icon={personOutline} slot="start" className="bp-reg-input-icon" />
                    <IonInput placeholder="Dlamini" value={form.lastName} onIonInput={(e) => set({ lastName: e.detail.value! })} />
                  </IonItem>
                  {errors.lastName && <IonNote color="danger" className="bp-reg-error">{errors.lastName}</IonNote>}
                </div>
              </div>
            </div>
          </div>
        );
      case 'profileType':
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">What describes you?</h2>
              <p className="bp-reg-step-sub">This helps clients know who they're working with.</p>
            </div>
            <div className="bp-reg-profile-grid">
              {(['Individual', 'Organisation'] as ProfileType[]).map((pt) => (
                <button
                  key={pt}
                  type="button"
                  className={`bp-reg-role-card ${form.profileType === pt ? 'selected' : ''}`}
                  onClick={() => set({ profileType: pt })}
                >
                  <span className="bp-reg-role-icon"><IonIcon icon={pt === 'Individual' ? person : business} /></span>
                  <div className="bp-reg-role-text">
                    <div className="bp-reg-role-title">{pt === 'Individual' ? 'Individual' : 'Company / Organisation'}</div>
                    <div className="bp-reg-role-desc">
                      {pt === 'Individual' ? 'Sole trader or skilled individual' : 'A business hiring or offering trade services'}
                    </div>
                  </div>
                  {form.profileType === pt && <IonIcon icon={checkmarkCircle} className="bp-reg-role-check" />}
                </button>
              ))}
            </div>
          </div>
        );
      case 'basicProfile':
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">Your profile at a glance</h2>
              <p className="bp-reg-step-sub">Just the essentials — you can add more later.</p>
            </div>
            <div className="bp-reg-fields">
              <div className="bp-reg-field">
                <label className="bp-reg-label">Occupational title</label>
                <div className="bp-reg-ofo" ref={headlineRef}>
                  <IonItem className={`bp-reg-input ${headlineOfo ? 'bp-reg-ofo-selected' : ''}`} lines="none">
                    <IonIcon icon={construct} slot="start" className="bp-reg-input-icon" />
                    <IonInput
                      placeholder="Search e.g. Electrician, Plumber…"
                      value={form.headline}
                      onIonInput={(e) => {
                        set({ headline: e.detail.value! });
                        runHeadlineSearch(e.detail.value!);
                      }}
                      onIonFocus={() => headlineSuggestions.length > 0 && setHeadlineOpen(true)}
                    />
                    {headlineLoading && <IonSpinner name="crescent" style={{ '--color': 'var(--bp-blue-400)' } as any} />}
                    {headlineOfo && <IonIcon icon={checkmarkCircle} className="bp-reg-ofo-check" />}
                  </IonItem>
                  {headlineOpen && headlineSuggestions.length > 0 && (
                    <div className="bp-reg-ofo-results">
                      <div className="bp-reg-ofo-results-head">Recognised Occupations</div>
                      {headlineSuggestions.map((ofo) => (
                        <button
                          key={ofo.code}
                          type="button"
                          className="bp-reg-ofo-row"
                          onClick={() => selectHeadline(ofo)}
                        >
                          <span className="bp-reg-ofo-title">{titleCase(ofo.description)}</span>
                          <span className="bp-reg-ofo-meta">{titleCase(ofo.majorGroup)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="bp-reg-ofo-hint">Only established occupational titles are allowed — pick yours from the list.</p>
                {errors.headline && <IonNote color="danger" className="bp-reg-error">{errors.headline}</IonNote>}
              </div>
              <div className="bp-reg-row">
                <div className="bp-reg-field">
                  <label className="bp-reg-label">Availability</label>
                  <IonItem className="bp-reg-input" lines="none">
                    <IonSelect
                      className="bp-reg-select"
                      value={form.availability}
                      onIonChange={(e) => set({ availability: e.detail.value })}
                    >
                      {availabilityEnum.map((a) => (
                        <IonSelectOption key={a} value={a}>{availabilityLabels[a]}</IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                </div>
                <div className="bp-reg-field">
                  <label className="bp-reg-label">Work type</label>
                  <IonItem className="bp-reg-input" lines="none">
                    <IonSelect
                      className="bp-reg-select"
                      value={form.workType}
                      onIonChange={(e) => set({ workType: e.detail.value })}
                    >
                      {workTypeEnum.map((w) => (
                        <IonSelectOption key={w} value={w}>{workTypeLabels[w]}</IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                </div>
              </div>
              <div className="bp-reg-row">
                <div className="bp-reg-field">
                  <label className="bp-reg-label">
                    Province
                    {form.province && geoDetected && (
                      <span className="bp-reg-detected"><IonIcon icon={location} /> Detected</span>
                    )}
                  </label>
                  <div className="bp-reg-ofo" ref={provinceRef}>
                    <IonItem className={`bp-reg-input ${(PROVINCES as string[]).includes(form.province) ? 'bp-reg-ofo-selected' : ''}`} lines="none">
                      <IonIcon icon={location} slot="start" className="bp-reg-input-icon" />
                      <IonInput
                        placeholder="Search SA provinces"
                        value={form.province}
                        onIonFocus={() => setProvinceOpen(true)}
                        onIonInput={(e) => {
                          set({ province: e.detail.value! });
                          setProvinceOpen(true);
                        }}
                      />
                      {(PROVINCES as string[]).includes(form.province) && (
                        <IonIcon icon={checkmarkCircle} className="bp-reg-ofo-check" />
                      )}
                    </IonItem>
                    {provinceOpen && (
                      <div className="bp-reg-ofo-results">
                        <div className="bp-reg-ofo-results-head">South African Provinces</div>
                        {provinceSuggestions.length === 0 ? (
                          <div className="bp-reg-ofo-row bp-reg-ofo-empty">No matching province</div>
                        ) : (
                          provinceSuggestions.map((p) => (
                            <button
                              key={p}
                              type="button"
                              className={`bp-reg-ofo-row ${form.province === p ? 'active' : ''}`}
                              onMouseDown={(e) => { e.preventDefault(); setProvince(p); }}
                            >
                              <span className="bp-reg-ofo-title">{p}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {errors.province && <IonNote color="danger" className="bp-reg-error">{errors.province}</IonNote>}
                </div>
                <div className="bp-reg-field">
                  <label className="bp-reg-label">
                    City / Suburb
                    {form.city && geoDetected && (
                      <span className="bp-reg-detected"><IonIcon icon={location} /> Detected</span>
                    )}
                  </label>
                  <div className="bp-reg-ofo" ref={cityRef}>
                    <IonItem className={`bp-reg-input ${citySuggestions.includes(form.city) ? 'bp-reg-ofo-selected' : ''}`} lines="none">
                      <IonIcon icon={location} slot="start" className="bp-reg-input-icon" />
                      <IonInput
                        placeholder="Search e.g. Sandton, Cape Town…"
                        value={form.city}
                        onIonFocus={() => setCityOpen(true)}
                        onIonInput={(e) => runCitySearch(e.detail.value!)}
                      />
                      {cityLoading && <IonSpinner name="crescent" style={{ '--color': 'var(--bp-blue-400)' } as any} />}
                      {citySuggestions.includes(form.city) && (
                        <IonIcon icon={checkmarkCircle} className="bp-reg-ofo-check" />
                      )}
                    </IonItem>
                    {cityOpen && citySuggestions.length > 0 && (
                      <div className="bp-reg-ofo-results">
                        <div className="bp-reg-ofo-results-head">Matching places</div>
                        {citySuggestions.map((name) => (
                          <button
                            key={name}
                            type="button"
                            className={`bp-reg-ofo-row ${form.city === name ? 'active' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); pickCity(name); }}
                          >
                            <span className="bp-reg-ofo-title">{name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.city && <IonNote color="danger" className="bp-reg-error">{errors.city}</IonNote>}
                </div>
              </div>
            </div>
          </div>
        );
      case 'nationalId':
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">ID verification</h2>
              <p className="bp-reg-step-sub">Optional — helps with verification and trust. SA ID numbers are validated against the Home Affairs checksum.</p>
            </div>
            <div className="bp-reg-fields">
              <div className="bp-reg-field">
                <label className="bp-reg-label">ID type</label>
                <IonItem className="bp-reg-input" lines="none">
                  <IonSelect
                    className="bp-reg-select"
                    value={form.nationalIdType}
                    onIonChange={(e) => set({ nationalIdType: e.detail.value })}
                  >
                    {nationalIdTypeEnum.map((t) => (
                      <IonSelectOption key={t} value={t}>{nationalIdLabels[t]}</IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
              </div>
              {form.nationalIdType === 'NationalId' && (
                <div className="bp-reg-field">
                  <label className="bp-reg-label">SA ID number (13 digits)</label>
                  <IonItem className="bp-reg-input" lines="none">
                    <IonIcon icon={card} slot="start" className="bp-reg-input-icon" />
                    <IonInput
                      placeholder="YYMMDD G SSS A C Z"
                      value={form.nationalIdNumber}
                      maxlength={13}
                      onIonInput={(e) => set({ nationalIdNumber: e.detail.value! })}
                    />
                  </IonItem>
                  <p className="bp-reg-ofo-hint">13-digit number. We validate the checksum against the official algorithm.</p>
                  {errors.number && <IonNote color="danger" className="bp-reg-error">{errors.number}</IonNote>}
                </div>
              )}
              {form.nationalIdType === 'Passport' && (
                <div className="bp-reg-field">
                  <label className="bp-reg-label">Passport number</label>
                  <IonItem className="bp-reg-input" lines="none">
                    <IonIcon icon={card} slot="start" className="bp-reg-input-icon" />
                    <IonInput
                      placeholder="e.g. AB1234567"
                      value={form.nationalIdNumber}
                      onIonInput={(e) => set({ nationalIdNumber: e.detail.value! })}
                    />
                  </IonItem>
                </div>
              )}
              {form.nationalIdType === 'TaxId' && (
                <div className="bp-reg-field">
                  <label className="bp-reg-label">SARS tax number</label>
                  <IonItem className="bp-reg-input" lines="none">
                    <IonIcon icon={card} slot="start" className="bp-reg-input-icon" />
                    <IonInput
                      placeholder="e.g. 0123456789"
                      value={form.nationalIdNumber}
                      onIonInput={(e) => set({ nationalIdNumber: e.detail.value! })}
                    />
                  </IonItem>
                </div>
              )}
              {errors.nationalId && <IonNote color="danger" className="bp-reg-error">{errors.nationalId}</IonNote>}
            </div>
          </div>
        );
      case 'skillSelection':
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">Pick your trades & skills</h2>
              <p className="bp-reg-step-sub">Clients search by these — pick what you offer, or continue without any.</p>
            </div>
            {state.context.submissionError && (
              <IonNote color="warning" className="bp-reg-error">{state.context.submissionError}</IonNote>
            )}
            <div className="bp-reg-skills">
              {tradeChips.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`bp-reg-skill ${form.skills.includes(s) ? 'selected' : ''}`}
                  onClick={() => toggleSkill(s)}
                >
                  {form.skills.includes(s) && <IonIcon icon={checkmark} />}
                  {s}
                </button>
              ))}
            </div>
            <p className="bp-reg-ofo-hint">Showing a sample of common trades &amp; skills — search below for the full official OFO list.</p>
            <div className="bp-reg-custom-skill bp-reg-ofo" ref={skillRef}>
              <IonItem className="bp-reg-input" lines="none">
                <IonIcon icon={construct} slot="start" className="bp-reg-input-icon" />
                <IonInput
                  placeholder="Search trades & skills — e.g. Web Developer…"
                  value={skillQuery}
                  onIonInput={(e) => runSkillSearch(e.detail.value!)}
                  onIonFocus={() => (skillSuggestions.length > 0 || skillNoResults) && setSkillOpen(true)}
                />
                {skillLoading && <IonSpinner name="crescent" style={{ '--color': 'var(--bp-blue-400)' } as any} />}
              </IonItem>
              {skillOpen && (skillSuggestions.length > 0 || skillNoResults) && (
                <div className="bp-reg-ofo-results">
                  <div className="bp-reg-ofo-results-head">Official OFO Trades &amp; Skills</div>
                  {skillSuggestions.map((o) => (
                    <button
                      key={o.code}
                      type="button"
                      className="bp-reg-ofo-row"
                      onClick={() => addSkill(o.description)}
                    >
                      <span className="bp-reg-ofo-title">{titleCase(o.description)}</span>
                      <span className="bp-reg-ofo-meta">{titleCase(o.majorGroup)}</span>
                    </button>
                  ))}
                  {skillSuggestions.length === 0 && (
                    <p className="bp-reg-ofo-empty">
                      No match found — try another word, or continue without selecting.
                    </p>
                  )}
                </div>
              )}
            </div>
            {form.skills.length > 0 && (
              <div className="bp-reg-chips">
                {form.skills.map((s) => (
                  <IonChip key={s} color="primary" className="bp-reg-custom-chip">
                    {s}
                    <IonIcon icon={close} onClick={() => toggleSkill(s)} />
                  </IonChip>
                ))}
              </div>
            )}
            {errors.skills && <IonNote color="danger" className="bp-reg-error">{errors.skills}</IonNote>}
            {state.context.role !== 'employer' && (
              <button type="button" className="bp-reg-skip" onClick={() => send({ type: 'SKIP_SKILLS' })}>
                Skip for now
              </button>
            )}
          </div>
        );
      case 'review':
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">Review &amp; submit</h2>
              <p className="bp-reg-step-sub">Double-check your details before we create your profile.</p>
            </div>
            {state.context.submissionError && (
              <IonNote color="danger" className="bp-reg-error bp-reg-review-error">{state.context.submissionError}</IonNote>
            )}
            <div className="bp-reg-review">
              <ReviewRow label="Role" value={selectedRole ?? ''} onEdit={() => send({ type: 'EDIT_ROLE' })} />
              <ReviewRow label="Account" value={`${form.firstName} ${form.lastName} · ${form.email}`} onEdit={() => send({ type: 'EDIT_ACCOUNT' })} />
              <ReviewRow label="Profile type" value={form.profileType} onEdit={() => send({ type: 'EDIT_PROFILE' })} />
              <ReviewRow label="Profile" value={`${form.headline} · ${form.workType} · ${form.city}, ${form.province}`} onEdit={() => send({ type: 'EDIT_PROFILE' })} />
              {form.nationalIdNumber && (
                <ReviewRow label="ID" value={`${nationalIdLabels[form.nationalIdType] || form.nationalIdType} · ${form.nationalIdNumber.replace(/(.{4})/g, '$1 ').trim()}`} onEdit={() => send({ type: 'EDIT_NATIONAL_ID' })} />
              )}
              {state.context.role !== 'employer' && (
                <ReviewRow label="Skills" value={form.skills.join(', ') || 'None'} onEdit={() => send({ type: 'EDIT_SKILLS' })} />
              )}
            </div>
          </div>
        );
      case 'submitting':
        return (
          <div className="bp-reg-center bp-reg-submitting">
            <IonSpinner />
            <p>Creating your BluePages profile…</p>
          </div>
        );
      case 'complete':
        return (
          <div className="bp-reg-center bp-reg-complete">
            <span className="bp-reg-complete-badge"><IonIcon icon={checkmark} /></span>
            <h2 className="bp-reg-step-title">You're registered!</h2>
            <IonText color="medium">
              {state.context.submissionError
                ? "Saved locally — we'll sync your profile once you sign in."
                : "We sent a verification link to your email. Open it to activate your account."}
            </IonText>
            <IonButton expand="block" className="bp-reg-submit" onClick={() => onComplete?.(state.context.role ?? undefined)}>
              Continue <IonIcon icon={arrowForward} slot="end" />
            </IonButton>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <IonPage className="bp-reg-page">
      <NoindexSeo />
      <IonContent className="bp-reg-content" fullscreen>
        <div className="bp-reg-shell">
          {/* Brand rail */}
          <aside className="bp-reg-brand">
            <div className="bp-glow-orb bp-auth-glow-1" />
            <div className="bp-glow-orb bp-auth-glow-2" />
            <div className="bp-reg-brand-inner">
              <LogoLink className="bp-auth-logo" />
              <div className="bp-reg-brand-head">
                <div className="bp-badge">
                  <IonIcon icon={sparkles} style={{ fontSize: '0.625rem' }} />
                  Join BluePages
                </div>
                <h1 className="bp-auth-brand-title">
                  Build your trade{' '}
                  <span className="bp-auth-brand-accent">profile</span>
                </h1>
                <p className="bp-auth-brand-sub">
                  A few quick steps to put your skills in front of homeowners
                  and businesses across South Africa.
                </p>
              </div>
              <ul className="bp-auth-highlights">
                <li className="bp-auth-highlight">
                  <span className="bp-auth-highlight-icon"><IonIcon icon={time} /></span>
                  <div><div className="bp-auth-highlight-title">Under 2 minutes</div><div className="bp-auth-highlight-desc">Quick, guided steps — no long forms</div></div>
                </li>
                <li className="bp-auth-highlight">
                  <span className="bp-auth-highlight-icon"><IonIcon icon={construct} /></span>
                  <div><div className="bp-auth-highlight-title">Matched by trade</div><div className="bp-auth-highlight-desc">Real contract work near you</div></div>
                </li>
                <li className="bp-auth-highlight">
                  <span className="bp-auth-highlight-icon"><IonIcon icon={people} /></span>
                  <div><div className="bp-auth-highlight-title">Verified & trusted</div><div className="bp-auth-highlight-desc">POPIA-compliant, scam-free marketplace</div></div>
                </li>
              </ul>
            </div>
          </aside>

          {/* Form panel */}
          <main className="bp-reg-panel">
            <div className="bp-reg-card">
              {!isComplete && !isSubmitting && (
                <div className="bp-reg-stepper">
                  {visibleSteps.map((s, i) => {
                    const meta = STEP_META[s];
                    const status = i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'todo';
                    const Icon = meta.icon as any;
                    return (
                      <div key={s} className={`bp-reg-step-dot ${status}`}>
                        <span className="bp-reg-step-num">
                          {status === 'done' ? <IonIcon icon={checkmark} /> : <IonIcon icon={Icon} />}
                        </span>
                        <span className="bp-reg-step-name">{meta.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="bp-reg-scroll">{renderStep()}</div>

              {!isComplete && (
                <div className="bp-reg-footer">
                  <IonButton
                    fill="clear"
                    className="bp-reg-back"
                    onClick={handleBack}
                    disabled={value === 'roleSelection' || value === 'idle'}
                  >
                    <IonIcon icon={arrowBack} slot="start" /> Back
                  </IonButton>
                  <IonButton onClick={handleNext} className="bp-reg-submit" disabled={isSubmitting}>
                    {isLastFormStep ? (
                      <>Create account <IonIcon icon={checkmark} slot="end" /></>
                    ) : (
                      <>Next <IonIcon icon={arrowForward} slot="end" /></>
                    )}
                  </IonButton>
                </div>
              )}
            </div>
          </main>
        </div>
        <AppFooter />
      </IonContent>
    </IonPage>
  );
}

function ReviewRow({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <div className="bp-reg-review-row">
      <div className="bp-reg-review-text">
        <div className="bp-reg-review-label">{label}</div>
        <div className="bp-reg-review-value">{value}</div>
      </div>
      <IonButton fill="clear" className="bp-reg-review-edit" onClick={onEdit}>
        <IonIcon icon={create} />
      </IonButton>
    </div>
  );
}
